import { FetchApiClient } from '../fetch-api-client'
import {
  RuleViolationsBatcher,
  type RuleViolationsBatcherOptions,
} from './rule-violations-batcher'

// Base path for the Rule Violations REST API. Matches the path used by the
// existing server- and client-side wrappers across package-plan and
// package-horizon-foundation, e.g. `rules/violations/search`,
// `rules/violations/findByTargetIds`.
// See https://developer.skedulo.com/skedulo-api/scheduling/rule-violations/
const BASE_PATH = 'rules/violations'

export type SortDirection = 'ASC' | 'DESC'

export type RuleViolationSortField =
  | 'createdTime'
  | 'description'
  | 'endTime'
  | 'id'
  | 'isCustom'
  | 'lastModifiedTime'
  | 'regionId'
  | 'ruleId'
  | 'sourceId'
  | 'sourceType'
  | 'startTime'
  | 'status'
  | 'targetId'
  | 'targetType'

export interface RuleViolationSortBy {
  field: RuleViolationSortField
  direction: SortDirection
}

export type RuleIdInclude = 'all' | 'only'

export interface RuleViolationRuleIdsFilter {
  core?: { include: RuleIdInclude }
  custom?: { include: RuleIdInclude }
}

export interface RuleViolationAssignedToFilter {
  userIds?: string[]
  includeUnassigned: boolean
}

export interface RuleViolationFilter {
  startTime: string
  endTime?: string
  targetTypes?: string[]
  regionIds?: string[]
  resourceIds?: string[]
  statuses?: string[]
  ruleIds?: RuleViolationRuleIdsFilter
  assignedTo?: RuleViolationAssignedToFilter
  includeNoTimestamp?: boolean
  targetIds?: string[]
}

export interface SearchRuleViolationsRequest {
  filter: RuleViolationFilter
  limit?: number
  offset?: number
  sortBy?: RuleViolationSortBy[]
}

export type RuleViolationStatus =
  | 'Open'
  | 'In Progress'
  | 'Closed'
  | 'Acknowledged'

export interface RuleViolationLink {
  type?: 'external' | 'skedulo'
  name?: string
  url?: string
}

export interface RuleViolation {
  id: number
  ruleId: string
  isCustom: boolean
  targetId: string
  sourceId: string
  targetType: string
  sourceType: string
  startTime: string | null
  endTime: string | null
  regionId?: string | null
  resourceIds?: string[]
  description?: string
  link?: RuleViolationLink
  status?: RuleViolationStatus | string
  assignedTo?: string | null
  context?: Record<string, unknown>
  createdTime: string
  lastModifiedTime: string
}

export interface SearchRuleViolationsResponse {
  violations: RuleViolation[]
  totalCount: number
  hasNextPage: boolean
  nextOffset?: number
}

export interface FindByTargetIdsRequest {
  targetIds: string[]
}

export interface FindByTargetIdsResponse {
  violations: RuleViolation[]
}

export class RuleViolationsApiClient {
  private readonly batcher: RuleViolationsBatcher

  constructor(
    protected readonly client: FetchApiClient,
    batcherOptions?: RuleViolationsBatcherOptions,
  ) {
    this.batcher = new RuleViolationsBatcher(
      (targetIds) => this.findByTargetIds({ targetIds }),
      batcherOptions,
    )
  }

  search(
    request: SearchRuleViolationsRequest,
  ): Promise<SearchRuleViolationsResponse> {
    return this.client.post<SearchRuleViolationsResponse>(
      `${BASE_PATH}/search`,
      {
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json' },
      },
    )
  }

  findByTargetIds(
    request: FindByTargetIdsRequest,
  ): Promise<FindByTargetIdsResponse> {
    return this.client.post<FindByTargetIdsResponse>(
      `${BASE_PATH}/findByTargetIds`,
      {
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json' },
      },
    )
  }

  getById(id: number | string): Promise<RuleViolation> {
    return this.client.get<RuleViolation>(`${BASE_PATH}/${id}`)
  }

  /**
   * Like `findByTargetIds`, but coalesces concurrent calls across hook
   * instances into a single POST per ~50 ms window and caches results
   * per-target for 30 s. Use this when many components independently need
   * violations for one target id each (e.g. a list of job cards).
   */
  findByTargetIdsBatched(targetId: string): Promise<RuleViolation[]> {
    return this.batcher.fetchByTargetId(targetId)
  }

  /**
   * Drops the batched cache. Pass a `targetId` to invalidate just that entry,
   * or omit it to clear everything (e.g. when the user pulls to refresh).
   */
  invalidateBatchCache(targetId?: string): void {
    this.batcher.invalidate(targetId)
  }
}
